import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

export default function Header() {
  const { lang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const base = `/${lang}`;
  const nav = [
    { key: "home", to: `${base}`, hash: "" },
    { key: "benefits", to: `${base}#benefits`, hash: "benefits" },
    { key: "process", to: `${base}#process`, hash: "process" },
    { key: "transparency", to: `${base}#transparency`, hash: "transparency" },
    { key: "newsletter", to: `${base}#newsletter`, hash: "newsletter" },
  ];

  const switchLang = (target) => {
    if (target === lang) return;
    const path = location.pathname;
    const newPath = path.replace(/^\/(ar|en)(?=\/|$)/, `/${target}`);
    navigate(`${newPath}${location.search}${location.hash || ""}`);
  };

  const isOnHome = location.pathname === base || location.pathname === `${base}/`;

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
    else window.scrollTo(0, 0);
  };

  const goCta = () => {
    setOpen(false);
    if (isOnHome) {
      scrollToId("newsletter");
    } else {
      navigate(`${base}#newsletter`);
    }
  };

  const handleNavClick = (item) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (!item.hash) {
      // Home link - navigate to home and scroll to top
      if (isOnHome) {
        window.scrollTo(0, 0);
      } else {
        navigate(base);
      }
      return;
    }
    if (isOnHome) {
      scrollToId(item.hash);
    } else {
      // navigate to home with hash; Home page will handle the scroll on mount
      navigate(`${base}#${item.hash}`);
    }
  };

  return (
    <header data-testid="site-header" className="w-full">
      {/* Top bar */}
      <div className="bg-[#071B34] text-white/80 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between">
          <span className="tracking-wide uppercase" data-testid="header-top-notice">
            {t("header.top_bar_notice")}
          </span>
          <span className="hidden sm:inline-block font-mono text-white/60">
            CUIT 30-71684920-4
          </span>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-[#0B2545] text-white border-b-4 border-[#C85A32]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
          {/* Brand */}
          <Link
            to={base}
            data-testid="header-brand-logo"
            className="flex items-center gap-3"
            onClick={() => setOpen(false)}
          >
            <div className="w-10 h-10 bg-[#C85A32] flex items-center justify-center">
              <span className="font-mono text-lg font-bold text-white">XQ</span>
            </div>
            <div className="leading-tight">
              <div className="font-bold tracking-widest text-lg">
                {t("header.brand_prefix")}
                <span className="ml-2 text-[10px] font-mono bg-white/10 border border-white/20 px-1.5 py-0.5 align-middle">
                  {t("header.brand_badge")}
                </span>
              </div>
              <div className="text-[11px] text-white/70 hidden sm:block">
                {t("meta.site_tagline")}
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="primary">
            {nav.map((item) => (
              <a
                key={item.key}
                href={item.to}
                data-testid={`nav-link-${item.key}`}
                onClick={handleNavClick(item)}
                className="px-3 py-2 text-sm font-medium text-white/90 hover:text-white hover:bg-white/10"
              >
                {t(`header.nav.${item.key}`)}
              </a>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Language switcher */}
            <div
              className="hidden sm:flex items-center border border-white/25"
              role="group"
              aria-label={t("language_switcher.aria_open")}
              data-testid="language-switcher"
            >
              <button
                type="button"
                onClick={() => switchLang("ar")}
                data-testid="language-switcher-ar"
                className={`px-3 py-1.5 text-xs font-semibold ${
                  lang === "ar" ? "bg-[#C85A32] text-white" : "text-white/80 hover:text-white"
                }`}
              >
                {t("language_switcher.es_short")}
              </button>
              <button
                type="button"
                onClick={() => switchLang("en")}
                data-testid="language-switcher-en"
                className={`px-3 py-1.5 text-xs font-semibold ${
                  lang === "en" ? "bg-[#C85A32] text-white" : "text-white/80 hover:text-white"
                }`}
              >
                {t("language_switcher.en_short")}
              </button>
            </div>

            <button
              type="button"
              onClick={goCta}
              data-testid="header-cta-button"
              className="hidden md:inline-flex items-center bg-[#C85A32] hover:bg-[#B04B27] text-white text-sm font-semibold px-4 py-2"
            >
              {t("header.cta_button")}
            </button>

            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle-button"
              aria-label={open ? t("header.menu_close") : t("header.menu_open")}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-white/25 text-white"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden bg-[#071B34] border-t border-white/10" data-testid="mobile-menu-panel">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-1">
              {nav.map((item) => (
                <a
                  key={item.key}
                  href={item.to}
                  data-testid={`mobile-nav-link-${item.key}`}
                  onClick={handleNavClick(item)}
                  className="px-3 py-3 text-sm font-medium text-white/90 border-b border-white/10"
                >
                  {t(`header.nav.${item.key}`)}
                </a>
              ))}
              <div className="flex items-center gap-2 pt-4">
                <button
                  onClick={() => switchLang("ar")}
                  data-testid="mobile-language-switcher-ar"
                  className={`flex-1 py-2 text-xs font-semibold border ${
                    lang === "ar" ? "bg-[#C85A32] border-[#C85A32] text-white" : "border-white/25 text-white/80"
                  }`}
                >
                  {t("language_switcher.es")}
                </button>
                <button
                  onClick={() => switchLang("en")}
                  data-testid="mobile-language-switcher-en"
                  className={`flex-1 py-2 text-xs font-semibold border ${
                    lang === "en" ? "bg-[#C85A32] border-[#C85A32] text-white" : "border-white/25 text-white/80"
                  }`}
                >
                  {t("language_switcher.en")}
                </button>
              </div>
              <button
                onClick={goCta}
                data-testid="mobile-header-cta-button"
                className="mt-3 bg-[#C85A32] hover:bg-[#B04B27] text-white text-sm font-semibold py-3"
              >
                {t("header.cta_button")}
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
